import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type JobProfile = { id: number; code: string; name: string; is_active: boolean }
type Department = { id: number; code: string; name: string; is_active: boolean }
type Subsidiary = { id: number; code: string; name: string }

type Position = {
  id: number
  code: string
  title: string
  job_profile: number | null
  job_profile_code: string | null
  job_profile_name: string | null
  department: number | null
  department_name: string | null
  subsidiary: number | null
  subsidiary_code: string | null
  subsidiary_name: string | null
  manager_position: number | null
  manager_position_code: string | null
  manager_position_title: string | null
  planned_start_date: string | null
  planned_end_date: string | null
  is_active: boolean
  is_open: boolean
  current_employment_id: number | null
  current_holder_name: string | null
  notes: string
}

type CreateInput = {
  title: string
  job_profile: number | null
  department: number | null
  subsidiary: number | null
  manager_position: number | null
  is_active: boolean
}

const FILTERS = ['ALL', 'OPEN', 'FILLED', 'INACTIVE'] as const
type Filter = typeof FILTERS[number]

export default function HRPositions() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const positionsQ = useQuery({
    queryKey: ['hr', 'positions'],
    queryFn: async () => (await api.get<Position[]>('/hr/positions/')).data,
  })
  // Seats a new position may report to: only those held by an eligible
  // people-manager (or vacant).
  const eligiblePositionsQ = useQuery({
    queryKey: ['hr', 'positions', 'manager-eligible'],
    queryFn: async () =>
      (await api.get<Position[]>('/hr/positions/?manager_eligible=1')).data,
  })
  const jobProfilesQ = useQuery({
    queryKey: ['hr', 'job-profiles'],
    queryFn: async () => (await api.get<JobProfile[]>('/hr/job-profiles/')).data,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })
  const subsidiariesQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => (await api.get<Subsidiary[]>('/subsidiaries/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filter, setFilter] = useState<Filter>('ALL')
  const [search, setSearch] = useState('')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Position>('/hr/positions/', draft)).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['hr', 'positions'] })
      toast.success('Position created')
      setCreating(false)
      navigate(`/admin/hr/positions/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/hr/positions/${id}/`),
    onSuccess: () => {
      toast.success('Position deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'positions'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const positions = positionsQ.data || []
  const jobProfiles = (jobProfilesQ.data || []).filter((j) => j.is_active)
  const departments = (departmentsQ.data || []).filter((d) => d.is_active)
  const subsidiaries = subsidiariesQ.data || []
  const allPositions = eligiblePositionsQ.data || []

  const filtered = positions.filter((p) => {
    if (filter === 'INACTIVE' && p.is_active) return false
    if (filter === 'OPEN' && (!p.is_active || !p.is_open)) return false
    if (filter === 'FILLED' && (!p.is_active || p.is_open)) return false
    if (filter === 'ALL') {/* no extra filter */}
    if (search.trim()) {
      const needle = search.trim().toLowerCase()
      const hay = `${p.code} ${p.title} ${p.job_profile_name ?? ''} ${p.department_name ?? ''}`.toLowerCase()
      if (!hay.includes(needle)) return false
    }
    return true
  })

  const openCount = positions.filter((p) => p.is_active && p.is_open).length
  const filledCount = positions.filter((p) => p.is_active && !p.is_open).length

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Positions</h2>
          <p className="text-sm text-muted-foreground">
            {positions.length} total · {openCount} open · {filledCount} filled
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New position</Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">Filter:</span>
        {FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded ${filter === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
        <div className="ml-auto w-64">
          <Input
            placeholder="Search by code, title, dept…"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
          />
        </div>
      </div>

      {positionsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          {positions.length === 0
            ? 'No positions yet. Create one to start mapping your org chart.'
            : 'No positions match this filter.'}
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Title</Th>
                <Th>Job profile</Th>
                <Th>Department</Th>
                <Th>Subsidiary</Th>
                <Th>Reports to</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/hr/positions/${p.id}`)}
                >
                  <Td className="font-mono text-xs">{p.code}</Td>
                  <Td>
                    <div className="font-medium text-foreground">{p.title}</div>
                    {p.current_holder_name && (
                      <div className="text-xs text-muted-foreground">held by {p.current_holder_name}</div>
                    )}
                  </Td>
                  <Td className="text-sm">
                    {p.job_profile_name
                      ? <span className="font-mono text-xs">{p.job_profile_code}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-sm">{p.department_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs font-mono uppercase">{p.subsidiary_code || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">
                    {p.manager_position_code || <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td>
                    {!p.is_active ? (
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-gray-100 text-gray-700">
                        Inactive
                      </span>
                    ) : p.is_open ? (
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-amber-100 text-amber-800">
                        Open
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-green-100 text-green-800">
                        Filled
                      </span>
                    )}
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/hr/positions/${p.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(`position ${p.code} — ${p.title}`, () => deleteMut.mutate(p.id))}
                          className="text-destructive"
                        >
                          Delete permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New position"
        description="A specific seat in the org chart. Fill in the rest (reporting line, dates) on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="p-title">Title *</FieldLabel>
            <Input id="p-title" autoFocus required value={draft.title}
              onChange={(ev) => setDraft({ ...draft, title: ev.target.value })}
              placeholder="Senior SWE on Platform Team" />
          </Field>

          <Field>
            <FieldLabel htmlFor="p-profile">Job profile</FieldLabel>
            <SearchableSelect
              id="p-profile"
              value={draft.job_profile ? String(draft.job_profile) : NONE}
              onChange={(v) => setDraft({ ...draft, job_profile: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...jobProfiles.map((j) => ({
                  value: String(j.id), label: j.name, hint: j.code,
                })),
              ]}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="p-dept">Department</FieldLabel>
              <SearchableSelect
                id="p-dept"
                value={draft.department ? String(draft.department) : NONE}
                onChange={(v) => setDraft({ ...draft, department: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...departments.map((d) => ({
                    value: String(d.id), label: d.name, hint: d.code,
                  })),
                ]}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="p-sub">Subsidiary</FieldLabel>
              <SearchableSelect
                id="p-sub"
                value={draft.subsidiary ? String(draft.subsidiary) : NONE}
                onChange={(v) => setDraft({ ...draft, subsidiary: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...subsidiaries.map((s) => ({
                    value: String(s.id), label: s.name, hint: s.code,
                  })),
                ]}
              />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="p-mgr">Reports to</FieldLabel>
            <SearchableSelect
              id="p-mgr"
              value={draft.manager_position ? String(draft.manager_position) : NONE}
              onChange={(v) => setDraft({ ...draft, manager_position: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...allPositions.map((p) => ({
                  value: String(p.id), label: p.title, hint: p.code,
                })),
              ]}
            />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    title: '',
    job_profile: null, department: null, subsidiary: null, manager_position: null,
    is_active: true,
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
